import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../u2000Core/axios/U2000HandleApiError";
import { extractBaseLanguage } from "../u2000Core/tools/U2000LanguageUtils";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import type { U2000AgingBucket } from "../u2000Core/constants/U2000AgingBuckets";
import { NS } from "./U3125";
import {
  doGetArAgingReport,
  doGetArAgingSummary,
  luHandleAppiError,
  parseU3125ArAgingReportResponse,
  parseU3125ArAgingSummaryResponse,
  u3125ArAgingCacheKey,
  u3125BuildArAgingRequestBody,
  u3125ReportToSummary,
  type U3125ArAgingReport,
  type U3125ArAgingSummaryReport,
} from "./U3125Service";

function isAxiosNotFound(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error != null &&
    "response" in error &&
    (error as { response?: { status?: number } }).response?.status === 404
  );
}

const fullReportFallbackCache = new Map<string, U3125ArAgingReport>();

export function u3125ClearArAgingFullReportFallbackCache(): void {
  fullReportFallbackCache.clear();
}

export function useU3125ArAgingSummaryHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [summary, setSummary] = useState<U3125ArAgingSummaryReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const lastRequestKeyRef = useRef<string>("");

  const loadSummary = useCallback(
    async (asOfDate: string, bucket?: U2000AgingBucket | null): Promise<U3125ArAgingSummaryReport | null> => {
      const trimmed = asOfDate?.trim().slice(0, 10);
      if (!trimmed || trimmed.length < 10) return null;
      const requestKey = u3125ArAgingCacheKey(trimmed, bucket);
      lastRequestKeyRef.current = requestKey;
      setIsLoading(true);
      try {
        const lang = extractBaseLanguage(i18n.language);
        const body = u3125BuildArAgingRequestBody({ asOfDate: trimmed, lang, bucket });
        let parsed: U3125ArAgingSummaryReport | null = null;
        try {
          const res = await doGetArAgingSummary(axios, body);
          parsed = parseU3125ArAgingSummaryResponse(res.data);
        } catch (error: unknown) {
          if (!isAxiosNotFound(error)) throw error;
          const fallbackKey = u3125ArAgingCacheKey(trimmed, bucket);
          let fullReport = fullReportFallbackCache.get(fallbackKey);
          if (!fullReport) {
            const res = await doGetArAgingReport(axios, body);
            fullReport = parseU3125ArAgingReportResponse(res.data) ?? undefined;
            if (fullReport) fullReportFallbackCache.set(fallbackKey, fullReport);
          }
          parsed = fullReport ? u3125ReportToSummary(fullReport, bucket) : null;
        }
        if (lastRequestKeyRef.current === requestKey) {
          setSummary(parsed);
        }
        return parsed;
      } catch (error: unknown) {
        if (lastRequestKeyRef.current === requestKey) {
          setSummary(null);
        }
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return null;
      } finally {
        if (lastRequestKeyRef.current === requestKey) {
          setIsLoading(false);
        }
      }
    },
    [axios, i18n.language, t, toast],
  );

  const clearSummary = useCallback(() => {
    setSummary(null);
  }, []);

  return { summary, isLoading, loadSummary, clearSummary, setSummary };
}
